import React from "react";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "components/mui";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import SendIcon from "@mui/icons-material/Send";
import type { SurveySummary } from "types/survey";

interface SurveyInserterPanelProps {
  surveys: SurveySummary[];
  selectedSurveyId: number | null;
  loading: boolean;
  onSurveyChange: (event: any) => void;
  onEdit: () => void;
  onCreateNew: () => void;
  onCancel: () => void;
  onInsert: () => void;
}

const SurveyInserterPanel: React.FC<SurveyInserterPanelProps> = ({
  surveys,
  selectedSurveyId,
  loading,
  onSurveyChange,
  onEdit,
  onCreateNew,
  onCancel,
  onInsert,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box 
      sx={{ 
        p: { xs: 1.5, sm: 2 }, 
        mb: 2,
        border: '1px solid',
        borderColor: 'grey.300',
        borderRadius: 2,
      }}
    >
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={2}
      >
        <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
          Insert Survey
        </Typography>
        <IconButton onClick={onCancel} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel id="survey-select-label">Select Survey</InputLabel>
            <Select
              labelId="survey-select-label"
              id="survey-select"
              value={
                selectedSurveyId && surveys.some(s => s.SurveyId === selectedSurveyId)
                  ? selectedSurveyId
                  : ""
              }
              label="Select Survey"
              onChange={onSurveyChange}
              disabled={loading}
              size="small"
            >
              {loading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading surveys...
                </MenuItem>
              ) : surveys.length === 0 ? (
                <MenuItem disabled>No surveys available</MenuItem>
              ) : (
                surveys.map((survey) => (
                  <MenuItem key={survey.SurveyId} value={survey.SurveyId}>
                    {survey.SurveyTitle}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Box 
            display="flex" 
            gap={1} 
            justifyContent={{ xs: 'stretch', sm: 'flex-start' }}
            flexDirection={{ xs: 'column', sm: 'row' }}
          >
            <Button
              variant="outlined"
              onClick={onEdit}
              disabled={!selectedSurveyId}
              fullWidth={isMobile}
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={onCreateNew}
              fullWidth={isMobile}
              startIcon={<AddIcon />}
            >
              Create
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={onInsert}
              disabled={!selectedSurveyId || loading}
              fullWidth={isMobile}
              startIcon={<SendIcon />}
            >
              Insert
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SurveyInserterPanel;
